/**
 * 붙여넣은 표를 **에디터 콘텐츠 너비에 맞춰** 각 열 비율을 유지한 채 축소한다.
 *
 * 배경: Word/docx 등에서 복사한 표는 열 너비(pt/px/%)가 에디터보다 넓어 가로 스크롤이
 * 생긴다. BlockNote 표는 열 너비를 블록 content의 `columnWidths` 배열로 모델링하며,
 * 붙여넣기 HTML 파서는 셀 width를 columnWidths로 읽지 않는다(붙여넣은 표는 기본폭).
 * 그래서 (1) 붙여넣기 전에 HTML에서 열별 원본 너비를 읽어 비율 유지로 maxWidth 이하로
 * 스케일하고, (2) 붙여넣은 뒤 새 표 블록의 `columnWidths`를 그 값으로 설정한다.
 * table-layout:fixed라 columnWidths 합이 에디터 너비 이하면 가로 스크롤이 없다.
 */

const MIN_COL_PX = 24;

function toPx(raw: string | null | undefined, maxWidth: number): number | null {
  if (!raw) return null;
  const m = String(raw).trim().match(/^([\d.]+)\s*(pt|px|%)?$/i);
  if (!m) return null;
  const v = parseFloat(m[1]);
  if (!Number.isFinite(v) || v <= 0) return null;
  const unit = (m[2] || "px").toLowerCase();
  if (unit === "pt") return v * (96 / 72);
  if (unit === "%") return (v / 100) * maxWidth;
  return v;
}

function elWidthPx(el: Element, maxWidth: number): number | null {
  const styleW = (el as HTMLElement).style?.width;
  return toPx(styleW, maxWidth) ?? toPx(el.getAttribute("width"), maxWidth);
}

/** 표의 열별 원본 너비(px)를 colgroup<col> 또는 첫 행 셀에서 읽는다. 못 구하면 null. */
function readColumnWidths(table: HTMLTableElement, maxWidth: number): number[] | null {
  const colEls = table.querySelector("colgroup")?.querySelectorAll("col");
  if (colEls && colEls.length > 0) {
    const widths: number[] = [];
    let ok = true;
    colEls.forEach((c) => {
      const span = parseInt(c.getAttribute("span") || "1", 10) || 1;
      const w = elWidthPx(c, maxWidth);
      if (w == null) ok = false;
      for (let i = 0; i < span; i++) widths.push(w ?? 0);
    });
    if (ok && widths.length > 0) return widths;
  }
  const firstRow = table.querySelector("tr");
  if (!firstRow) return null;
  const widths: number[] = [];
  let ok = true;
  Array.from(firstRow.children).forEach((cell) => {
    if (cell.tagName !== "TD" && cell.tagName !== "TH") return;
    const span = parseInt(cell.getAttribute("colspan") || "1", 10) || 1;
    const w = elWidthPx(cell, maxWidth);
    if (w == null) ok = false;
    const per = (w ?? 0) / span;
    for (let i = 0; i < span; i++) widths.push(per);
  });
  return ok && widths.length > 0 ? widths : null;
}

/** 원본 너비를 maxWidth 이하로 비율 유지 스케일한 px 배열. 넘지 않으면 원본 유지. */
function fitWidths(widths: number[], maxWidth: number): number[] {
  const total = widths.reduce((a, b) => a + b, 0);
  if (total <= 0) return widths;
  const scale = total > maxWidth ? maxWidth / total : 1;
  return widths.map((w) => Math.max(MIN_COL_PX, Math.round(w * scale)));
}

/**
 * 붙여넣기 HTML의 각 `<table>`에 대해, 에디터 너비(maxWidth)에 맞춘 columnWidths 배열을
 * HTML 등장 순서로 반환한다. 너비를 못 읽은 표는 null.
 */
export function computeFittedColumnWidthsPerTable(
  html: string,
  maxWidth: number,
): (number[] | null)[] {
  if (!html || typeof DOMParser === "undefined" || !(maxWidth > 0)) return [];
  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(html, "text/html");
  } catch {
    return [];
  }
  return Array.from(doc.querySelectorAll("table")).map((t) => {
    const widths = readColumnWidths(t as HTMLTableElement, maxWidth);
    return widths ? fitWidths(widths, maxWidth) : null;
  });
}

/** blocks(및 children)에서 table 블록을 등장 순서로 모은다. */
export function collectTableBlocks(blocks: any[]): any[] {
  const out: any[] = [];
  const walk = (bs: any[]) => {
    for (const b of bs) {
      if (b?.type === "table") out.push(b);
      if (b?.children?.length) walk(b.children);
    }
  };
  walk(blocks || []);
  return out;
}

/**
 * 붙여넣기 직후 호출: beforeIds에 없던 새 표 블록들에 perTable[i] columnWidths를 적용한다.
 * 열 개수가 맞을 때만 적용(불일치 시 건너뜀).
 */
export function applyFittedWidthsToNewTables(
  editor: any,
  beforeIds: Set<string>,
  perTable: (number[] | null)[],
): void {
  if (!editor || perTable.length === 0) return;
  const newTables = collectTableBlocks(editor.document).filter(
    (b) => !beforeIds.has(b.id),
  );
  newTables.forEach((tb, i) => {
    const widths = perTable[i];
    const current = tb?.content?.columnWidths;
    if (
      widths &&
      Array.isArray(current) &&
      current.length === widths.length
    ) {
      try {
        editor.updateBlock(tb, {
          type: "table",
          content: { ...tb.content, columnWidths: widths },
        });
      } catch {
        /* 단일 표 실패가 전체 붙여넣기를 막지 않도록 무시 */
      }
    }
  });
}
